/**
 * JobStatusBadge Component
 * Shows active job count in navigation with dropdown details
 */
import React, { useState } from 'react';
import { Bell, Loader2, CheckCircle, XCircle, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';

const JobStatusBadge = ({ 
  activeJobs = [], 
  onCancelJob,
  permissionGranted,
  onRequestPermission 
}) => {
  const [open, setOpen] = useState(false);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'FAILED':
      case 'TIMEOUT':
      case 'CANCELLED':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'QUEUED':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      default:
        return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-green-500';
      case 'FAILED':
      case 'TIMEOUT':
      case 'CANCELLED':
        return 'bg-red-500';
      case 'QUEUED':
        return 'bg-yellow-500';
      default:
        return 'bg-blue-500';
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="relative"
          data-testid="job-status-badge"
        >
          <Bell className="w-5 h-5" />
          {activeJobs.length > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-emerald-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
              {activeJobs.length}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Processing Jobs</span>
          {!permissionGranted && (
            <Button 
              variant="link" 
              size="sm" 
              className="text-xs h-auto p-0"
              onClick={onRequestPermission}
            >
              Enable Notifications
            </Button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {activeJobs.length === 0 ? (
          <div className="px-2 py-4 text-center text-muted-foreground text-sm">
            No active processing jobs
          </div>
        ) : (
          activeJobs.map((job) => (
            <DropdownMenuItem 
              key={job.jobId} 
              className="flex flex-col items-start gap-2 p-3 cursor-default"
              onSelect={(e) => e.preventDefault()}
            >
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-2">
                  {getStatusIcon(job.status)}
                  <span className="font-medium text-sm truncate max-w-[180px]">
                    {job.filename || 'Document'}
                  </span>
                </div>
                <Badge variant="outline" className={`text-xs ${getStatusColor(job.status)} text-white`}>
                  {job.status}
                </Badge>
              </div>
              <div className="w-full">
                <Progress value={job.progress} className="h-1.5" />
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-muted-foreground">
                    {Math.round(job.progress)}%
                  </span>
                  {job.status !== 'COMPLETED' && job.status !== 'FAILED' && (
                    <Button
                      variant="link"
                      size="sm"
                      className="text-xs h-auto p-0 text-red-500"
                      onClick={() => onCancelJob?.(job.jobId)}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default JobStatusBadge;
